import { useState } from "react";
import { Button, Chip, Paper, Stack, TextField as MuiTextField, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { DateField, Show, SimpleShowLayout, TextField, useNotify, useRecordContext, useRefresh } from "react-admin";

import { adminApi } from "../../dataProvider";
import { PAYOUT_METHOD_LABEL, payoutStatus } from "../partners/status";
import type { PayoutRecord } from "./List";

// Подписи реквизитов по способу — те же поля, что и в выводе клиента.
const DEST_ROWS: Record<string, { key: string; label: string }[]> = {
  card: [
    { key: "card_number", label: "Номер карты" },
    { key: "first_name", label: "Имя" },
    { key: "last_name", label: "Фамилия" },
  ],
  swift: [
    { key: "beneficiary_name", label: "Получатель" },
    { key: "iban", label: "IBAN / счёт" },
    { key: "swift", label: "SWIFT/BIC" },
    { key: "bank_name", label: "Банк" },
    { key: "bank_country", label: "Страна банка" },
  ],
  crypto: [
    { key: "wallet_address", label: "Кошелёк (TRC20)" },
    { key: "network", label: "Сеть" },
  ],
};

function StatusChip() {
  const record = useRecordContext<PayoutRecord>();
  if (!record) return null;
  const s = payoutStatus(record.status);
  return <Chip size="small" color={s.color} label={s.label} />;
}

function Destination() {
  const record = useRecordContext<PayoutRecord>();
  if (!record) return null;
  if (record.method === "internal") {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Перевод на счёт партнёра</Typography>
        <Typography variant="body2">
          Зачислено: <b>{Number(record.amount_rub ?? 0).toLocaleString("ru-RU")} ₽</b> по курсу{" "}
          {Number(record.rate ?? 0).toFixed(2)}. Исполняется автоматически, оператор не участвует.
        </Typography>
      </Paper>
    );
  }
  const rows = DEST_ROWS[record.method] ?? [];
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Реквизиты выплаты</Typography>
      <Stack spacing={0.5}>
        {rows.map((row) => (
          <Stack key={row.key} direction="row" justifyContent="space-between" sx={{ maxWidth: 460 }}>
            <Typography variant="body2" color="text.secondary">{row.label}</Typography>
            <Typography variant="body2" fontWeight={600}>{record.destination?.[row.key] || "—"}</Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

function Amounts() {
  const record = useRecordContext<PayoutRecord>();
  if (!record) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Суммы</Typography>
      <Stack spacing={0.5} sx={{ maxWidth: 320 }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">Списано с баланса</Typography>
          <Typography variant="body2">{Number(record.amount_usd).toFixed(2)} $</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">Комиссия</Typography>
          <Typography variant="body2">{Number(record.fee_usd).toFixed(2)} $</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">К выплате</Typography>
          <Typography variant="body2" fontWeight={700}>{Number(record.net_usd).toFixed(2)} $</Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

function Actions() {
  const record = useRecordContext<PayoutRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  const [tx, setTx] = useState("");
  const [reason, setReason] = useState("");
  if (!record || record.status !== "pending") return null;

  const call = async (action: "complete" | "reject", json?: unknown) => {
    setBusy(true);
    try {
      await adminApi(`/admin/partner-payouts/${record.id}/${action}`, { method: "POST", json });
      notify("Готово", { type: "success" });
      refresh();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Ошибка", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Действия</Typography>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {record.method === "crypto" && (
            <MuiTextField size="small" label="tx hash" value={tx} onChange={(e) => setTx(e.target.value)} />
          )}
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            disabled={busy}
            onClick={() => call("complete", tx ? { tx_hash: tx } : {})}
          >
            Выплата исполнена
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <MuiTextField
            size="small"
            label="Причина отклонения"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Button color="error" startIcon={<CancelIcon />} disabled={busy} onClick={() => call("reject", { reason })}>
            Отклонить
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          При отклонении сумма возвращается партнёру автоматически — баланс считается как
          начисления минус выплаты.
        </Typography>
      </Stack>
    </Paper>
  );
}

export function PartnerPayoutShow() {
  return (
    <Show>
      <SimpleShowLayout>
        <TextField source="reference" label="Заявка" />
        <StatusChip />
        <TextField source="partner_phone" label="Партнёр" />
        <MethodLine />
        <TextField source="reject_reason" label="Причина отклонения" />
        <TextField source="tx_hash" label="tx hash" />
        <DateField source="created_at" label="Создана" showTime />
        <DateField source="completed_at" label="Исполнена" showTime />
        <Amounts />
        <Destination />
        <Actions />
      </SimpleShowLayout>
    </Show>
  );
}

function MethodLine() {
  const record = useRecordContext<PayoutRecord>();
  if (!record) return null;
  return (
    <Typography variant="body2">
      Способ: <b>{PAYOUT_METHOD_LABEL[record.method] ?? record.method}</b>
    </Typography>
  );
}
